// Better Auth REST client (apps/api/src/auth/better-auth.ts:15 AUTH_BASE_PATH =
// "/api/auth"; docs/architecture/contracts.md's onboarding row: "Better Auth cookie +
// global AccessGuard"). Slice 0 shipped no web client convention (apps/web did not
// exist yet - this is the first frontend, per slice-2-onboarding.md:8), so this is a
// minimal fetch client against Better Auth's own documented REST surface
// (POST {base}/sign-up/email, POST {base}/sign-in/email, POST {base}/sign-out,
// GET {base}/get-session), not the full better-auth client SDK (keeping the dependency
// surface minimal - one more package this lane would need to justify and pin). The
// response schemas below are a narrow, LOCAL parse of just the fields this app reads
// (email); Better Auth's full session/user shape is not in @nightcrew/shared and is
// intentionally not modeled beyond what is consumed here.
import { z } from "zod";
import type { Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

const AUTH_BASE = "/api/auth";

const SessionUser = z.object({ id: z.string(), email: z.email(), name: z.string().nullable() });
export type SessionUser = z.infer<typeof SessionUser>;

const GetSessionResponse = z.object({ user: SessionUser }).nullable();

/** `null` when there is no active session (an unauthenticated request, not an error). */
export const getSession = (): Promise<Result<{ user: SessionUser } | null, ClientError>> =>
  apiFetch(`${AUTH_BASE}/get-session`, GetSessionResponse);

const SignInResponse = z.object({ user: SessionUser, redirect: z.boolean().optional() });

export const signInWithEmail = (
  email: string,
  password: string,
): Promise<Result<z.infer<typeof SignInResponse>, ClientError>> =>
  apiPost(`${AUTH_BASE}/sign-in/email`, SignInResponse, { email, password });

const SignUpResponse = z.object({ user: SessionUser });

export const signUpWithEmail = (
  email: string,
  password: string,
  name: string,
): Promise<Result<z.infer<typeof SignUpResponse>, ClientError>> =>
  apiPost(`${AUTH_BASE}/sign-up/email`, SignUpResponse, { email, password, name });

export const signOut = (): Promise<Result<Record<string, never>, ClientError>> =>
  apiPost(`${AUTH_BASE}/sign-out`, z.object({}), {});
